import { useGate, useStore } from 'effector-react'
import {
  $cars,
  $carsStageGate,
  $failed,
  $fetching,
  $pagesLoaded,
  $totalCount,
  handleIsLoadMoreCars,
  loadNextPage
} from 'features/cars-list/model'
import { CreditFilterForm } from 'features/credit-filter-form/components/index'
import { getProjectName } from 'lib/get-project-name'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useAVNConfig } from 'stages/AVN'
import { useTheme } from 'styled-components'
import { Button } from 'ui/button'
import { CarCardsList } from 'ui/car-cards-list'
import { Cell, Row } from 'ui/grid'
import { SkeletonCard } from 'ui/skeleton-card'
import { StageError } from 'ui/stage-error'

import { getCarsByAllDealers } from '../../../api/cars'
import { cloneDeclarationSection } from '../../../lib/clone-declaration-section'
import { getDealerInfoById } from '../../../lib/get-dealer-by-id'
import { getUncheckedDealerWithCars } from '../../../lib/get-unchecked-dealer-with-cars'
import { logger } from '../../../lib/logger'
import { $creditFilterEnabled, setCreditAvailable } from '../../credit-filter-form/model'
import { $declaration, $internalFilterValues, mergeValues, setNextDealer } from '../../filter/model'
import { carDataToSCCar, useListSmartContractAPI } from '../../smart-contract/list/useListSmartContractAPI'
import { $listRequestData } from '../model/init'
import { CommercialFilters } from './CommercialFilters'
import { GenericV2CommercialFilters } from './GenericV2CommercialFilters'
import { SelectedFilters } from './SelectedFilters'
import { ShowNextDealer } from './ShowNextDealer'

export const CarsList: FC<{}> = () => {
  const props = useAVNConfig()
  const theme = useTheme()
  const [creditFilterFormOpen, setCreditFilterFormOpen] = useState(false)
  useGate($carsStageGate, { carsListProps: props, appTheme: theme })

  const cars = useStore($cars)
  const pagesLoaded = useStore($pagesLoaded)
  const totalCount = useStore($totalCount)
  const filters = useStore($internalFilterValues)
  const declaration = useStore($declaration)
  const failed = useStore($failed)
  const fetching = useStore($fetching)
  const creditFilterEnabled = useStore($creditFilterEnabled)
  const listRequestData = useStore($listRequestData)
  const [isButtonHidden, setIsButtonHidden] = useState(false)
  const { init, api, state } = useListSmartContractAPI()

  const skeletons = useMemo(() => {
    const pageSize = props.pageLength || 25
    const imageRatio = props.carViewSettings.images.ratio || { x: 16, y: 9 }
    return Array.from({ length: pageSize * pagesLoaded }, (_, i) => {
      return (
        <Cell size={12} phablet={6} projectors={4} key={i} gutters={true}>
          <SkeletonCard ratio={imageRatio} />
        </Cell>
      )
    })
  }, [])

  const handleCarsNextDealer = async () => {
    if (pagesLoaded * (props?.pageLength || 0) >= totalCount) {
      const dealers = filters?.dealer ? [...filters?.dealer] : []

      const dealerSection = cloneDeclarationSection('dealer', declaration)

      // мутируем дилеров, чтобы оставить тех, которые не выбраны
      if (dealerSection) {
        dealerSection?.groups.forEach((group) => {
          group.fields = group.fields.filter((field) => !dealers.includes(field.value || ''))
        })
      }

      if (filters) {
        // ищем невыбранного дилера, если у него есть машины, получаем его id
        const uncheckedDealer = await getUncheckedDealerWithCars(dealerSection, listRequestData, filters)
        if (uncheckedDealer) {
          // добавляем в массив, который дальше используем для запроса с обновленным количеством дилеров
          dealers.push(uncheckedDealer)
          // делаем запрос, чтобы получить данные о текущем дилере (адрес, название)
          const dealerInfo = await getDealerInfoById(uncheckedDealer, props.dealerInfoUrl)
          // добавляем в массив информацию об этом дилере
          // ключ - порядковый номер машины, перед которой будем показывать надпись
          setNextDealer({ [totalCount]: <ShowNextDealer nextDealerInfo={dealerInfo} /> })
          // делаем запрос на сервер с обновлёнными дилерами
          mergeValues({ dealer: dealers })
        }
      }
    }
  }

  // проверка, когда скрывать кнопку "показать ещё"
  // если количество загруженых машин на странице равно всем машинам, которые возможно получить, то скрываем кнопку
  useEffect(() => {
    const handleButtonShowMore = async () => {
      if (props.showCarsNextDealer) {
        const allDealers =
          cloneDeclarationSection('dealer', declaration)?.groups.flatMap((group) =>
            group.fields.map((field) => field?.value || '')
          ) || []

        const response = await getCarsByAllDealers(props, allDealers, filters)
        setIsButtonHidden(totalCount >= (response?.count || 0))
      }
    }

    handleButtonShowMore()
  }, [totalCount, pagesLoaded])

  const showSkeletons = fetching && !cars

  useEffect(() => {
    if (!props.smartContractInstalled) {
      setCreditAvailable(false)
    }
    if (!failed && !fetching && cars && props.smartContractInstalled) {
      logger.log('[SC/updateCarList]', carDataToSCCar(cars))
      init(cars, props.isNew)
      api.updateCarList(carDataToSCCar(cars))
    }
  }, [fetching, cars, failed])

  return (
    <>
      <CreditFilterForm onClose={() => setCreditFilterFormOpen(false)} show={creditFilterFormOpen} />

      {failed ? (
        <Row>
          <Cell size={12} gutters={true}>
            <StageError text={props.i18n.loadingError} />
          </Cell>
        </Row>
      ) : (
        <>
          {getProjectName() !== 'generic_v2' ? (
            <CommercialFilters openCreditFilterForm={() => setCreditFilterFormOpen(true)} />
          ) : (
            <GenericV2CommercialFilters openCreditFilterForm={() => setCreditFilterFormOpen(true)} />
          )}
          <SelectedFilters />
          <CarCardsList
            showCreditPrice={true}
            openCardsLinksInNewTab={props.openCardsLinksInNewTab}
            carViewSettings={props.carViewSettings}
            useSmartContract={
              props.smartContractInstalled &&
              (creditFilterEnabled || Boolean(state && state.tradeIn.behaviourByUser === 'applied'))
            }
            cars={cars}
            emptyText={props.i18n.noCarsFound}
            showCarsNextDealer={props.showCarsNextDealer}
            showSkeletons={showSkeletons}
            skeletons={skeletons}
            showVideoIcon={props.showVideoIcon}
            cardSize={{
              size: 12,
              phablet: 6,
              projectors: 4
            }}
          />
          {((!isButtonHidden && props.showCarsNextDealer) ||
            (props.pageLength && pagesLoaded * props.pageLength < totalCount)) && (
            <Row justifyContent={'center'} alignItems={'center'}>
              <Cell auto>
                <Button
                  onClick={(e) => {
                    loadNextPage(e)

                    if (props.showCarsNextDealer) {
                      handleIsLoadMoreCars(true)
                      handleCarsNextDealer()
                    }
                  }}
                  disabled={fetching}
                  size={'big'}
                  icon={'arrow-down'}
                >
                  {props.i18n.loadMore}
                </Button>
              </Cell>
            </Row>
          )}
        </>
      )}
    </>
  )
}
